import {isUiReadinessEnabled, type CopilotUiReadinessView, startCopilotRootReadiness} from './copilot-ui-readiness'
import {CopilotUiReadinessContext, useCopilotUiReadinessLifetime} from './copilot-ui-readiness-context'
import {Outlet, type LoaderFunctionArgs, useLoaderData} from '@github-ui/react-router'
import {useEffect, useLayoutEffect, useSyncExternalStore} from 'react'

import {DataRouterApplication} from '@github-ui/react-core/future/data-router-application'
import {registerDataRouterApp} from '@github-ui/react-core/register-app'
import {RootAppRouteErrorElement} from '@github-ui/react-core/future/RouterErrorBoundary'
import {
  CopilotResultPresentationRootProvider,
  startCopilotResultPresentationRoot,
  type CopilotResultPresentationRoot,
} from './copilot-result-presentation-root'

const previousViews = new WeakMap<HTMLElement, CopilotUiReadinessView>()

interface CopilotRouteLoad {
  readonly view: CopilotUiReadinessView | undefined
  readonly url: string
  readonly provisional: boolean
}

interface CopilotPresentationContextStore {
  getSnapshot(): CopilotResultPresentationRoot | undefined
  subscribe(listener: () => void): () => void
  publish(root: CopilotResultPresentationRoot): void
}

function createPresentationContextStore(
  initial: CopilotResultPresentationRoot | undefined,
): CopilotPresentationContextStore {
  let snapshot = initial
  const listeners = new Set<() => void>()
  return {
    getSnapshot: () => snapshot,
    subscribe: listener => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    publish: root => {
      snapshot = root
      for (const listener of listeners) listener()
    },
  }
}

function CopilotRouteError({
  appName,
  presentation,
  onError,
}: {
  appName: string
  presentation: CopilotResultPresentationRoot | undefined
  onError: () => void
}) {
  useEffect(() => {
    onError()
  }, [onError])
  return (
    <CopilotResultPresentationRootProvider root={presentation}>
      <RootAppRouteErrorElement appName={appName} />
    </CopilotResultPresentationRootProvider>
  )
}

function CopilotReadinessRoot({
  initialLoad,
  presentation,
  presentationContext,
  onCommit,
}: {
  initialLoad: CopilotRouteLoad
  presentation: CopilotResultPresentationRoot | undefined
  presentationContext: CopilotPresentationContextStore
  onCommit: (load: CopilotRouteLoad) => boolean
}) {
  const loaderData = useLoaderData<CopilotRouteLoad | undefined>()
  const load = loaderData ?? initialLoad
  const {view} = load
  const contextRoot = useSyncExternalStore(
    presentationContext.subscribe,
    presentationContext.getSnapshot,
    presentationContext.getSnapshot,
  )
  useCopilotUiReadinessLifetime(view, 'replaced')
  useLayoutEffect(() => {
    if (onCommit(load) && presentation) presentationContext.publish({...presentation})
  }, [load, onCommit, presentation, presentationContext])
  useEffect(() => {
    view?.initialized()
  }, [view])

  return (
    <CopilotUiReadinessContext value={view}>
      <CopilotResultPresentationRootProvider root={presentation} contextRoot={contextRoot}>
        <Outlet />
      </CopilotResultPresentationRootProvider>
    </CopilotUiReadinessContext>
  )
}

class CopilotApp<T extends string> extends DataRouterApplication<T> {
  readonly #app: DataRouterApplication<T>

  constructor(app: DataRouterApplication<T>) {
    super(app.name, [], {tanStackRouterEnabled: app.tanStackRouterEnabled})
    this.#app = app
  }

  override registration(options?: Parameters<DataRouterApplication<T>['registration']>[0]) {
    if (!isUiReadinessEnabled()) return this.#app.registration(options)

    const element =
      typeof document === 'undefined'
        ? null
        : [...document.querySelectorAll<HTMLElement>('react-app[app-name]')].find(
            root => root.getAttribute('app-name') === this.#app.name,
          )
    const previous = element ? previousViews.get(element) : undefined
    const view =
      typeof window === 'undefined'
        ? undefined
        : startCopilotRootReadiness(this.#app.name, previous?.ready ? undefined : previous?.open)
    if (view && previous && previous !== view) previous.dispose('replaced')
    const presentation = element ? startCopilotResultPresentationRoot(this.#app.name) : undefined
    const presentationContext = createPresentationContextStore(presentation)
    const initialUrl = typeof window === 'undefined' ? '/' : window.location.href
    let activePath = typeof window === 'undefined' ? undefined : window.location.pathname
    let activeView = view
    const provisionalLoads = new Set<CopilotRouteLoad>()
    const provisionalAborts = new WeakMap<CopilotRouteLoad, {signal: AbortSignal; listener: () => void}>()
    const configureView = (next: CopilotUiReadinessView | undefined) => {
      if (!next) return
      next.observeOrigin(options?.embeddedData)
      next.configurationAvailable(undefined, true)
    }
    const rememberView = (next: CopilotUiReadinessView | undefined) => {
      if (!element) return
      if (next) previousViews.set(element, next)
      else previousViews.delete(element)
    }
    const releaseAbort = (load: CopilotRouteLoad) => {
      const abort = provisionalAborts.get(load)
      if (!abort) return
      abort.signal.removeEventListener('abort', abort.listener)
      provisionalAborts.delete(load)
    }
    const discardProvisionalLoad = (load: CopilotRouteLoad) => {
      if (!provisionalLoads.delete(load)) return
      releaseAbort(load)
      load.view?.dispose('replaced')
    }
    const discardProvisionalLoads = () => {
      for (const load of provisionalLoads) discardProvisionalLoad(load)
    }
    const commitLoad = (load: CopilotRouteLoad) => {
      if (load.provisional && !provisionalLoads.delete(load)) return false
      releaseAbort(load)
      discardProvisionalLoads()
      const url = new URL(load.url, window.location.origin)
      presentation?.navigate(url)
      activePath = url.pathname
      activeView = load.view
      rememberView(activeView)
      return load.provisional
    }
    const failRoute = () => {
      const url = new URL(window.location.href, window.location.origin)
      const destination = [...provisionalLoads].at(-1)
      const failedView = destination ? destination.view : activeView
      failedView?.failure('frontend_error')
      discardProvisionalLoads()
      activePath = url.pathname
      activeView = undefined
      rememberView(undefined)
      presentation?.navigate(url)
      presentation?.failed()
    }

    try {
      const {routes} = this.#app.registration(options)
      configureView(activeView)
      rememberView(activeView)
      const initialLoad: CopilotRouteLoad = {view, url: initialUrl, provisional: false}
      return {
        routes: [
          {
            Component: () => (
              <CopilotReadinessRoot
                initialLoad={initialLoad}
                presentation={presentation}
                presentationContext={presentationContext}
                onCommit={commitLoad}
              />
            ),
            ...(activeView || presentation
              ? {
                  errorElement: (
                    <CopilotRouteError appName={this.#app.name} presentation={presentation} onError={failRoute} />
                  ),
                  loader: ({request}: LoaderFunctionArgs) => {
                    const url = new URL(request.url, window.location.origin)
                    if (url.pathname === activePath) {
                      return {view: activeView, url: url.href, provisional: false} satisfies CopilotRouteLoad
                    }
                    const next = startCopilotRootReadiness(this.#app.name, undefined, undefined, url)
                    configureView(next)
                    const load = {view: next, url: url.href, provisional: true} satisfies CopilotRouteLoad
                    provisionalLoads.add(load)
                    const listener = () => discardProvisionalLoad(load)
                    provisionalAborts.set(load, {signal: request.signal, listener})
                    if (request.signal.aborted) listener()
                    else request.signal.addEventListener('abort', listener, {once: true})
                    return load
                  },
                  shouldRevalidate: () => true,
                }
              : {}),
            children: routes,
          },
        ],
      }
    } catch (error) {
      view?.failure('registration')
      presentation?.failed()
      presentation?.dispose()
      throw error
    }
  }
}

export function registerCopilotApp<T extends string>(app: DataRouterApplication<T>) {
  registerDataRouterApp(new CopilotApp(app))
}
